import AWS from "aws-sdk";
import { S3Error } from "~/error-handling/exceptions";

export interface TransferProgress {
  loaded: number;
  total?: number;
  percent?: number;
}

// Note: test function to check working with bucket through AWS SDK
export const listObjects = async (
  s3: AWS.S3,
  bucketName: string,
): Promise<AWS.S3.ListObjectsOutput> => {
  const params: AWS.S3.ListObjectsRequest = {
    Bucket: bucketName,
  };
  try {
    const response = await s3.listObjects(params).promise();
    return response;
  } catch (error) {
    throw new S3Error("listObjects", bucketName, "", error);
  }
};

export const uploadFile = async (
  s3: AWS.S3,
  bucketName: string,
  key: string,
  file: File,
  options: {
    onProgress?: (progress: TransferProgress) => void;
    useTransferAccelerationOverride?: boolean;
    disableAcceleration?: boolean;
    contentType?: string;
  } = {},
): Promise<AWS.S3.ManagedUpload.SendData> => {
  const {
    onProgress,
    useTransferAccelerationOverride = false,
    disableAcceleration = false,
    contentType,
  } = options;

  try {
    const params: AWS.S3.PutObjectRequest = {
      Bucket: bucketName,
      Key: key,
      Body: file,
      ...(contentType && { ContentType: contentType }),
    };

    const client = disableAcceleration
      ? new AWS.S3({ ...s3.config, useAccelerateEndpoint: false })
      : useTransferAccelerationOverride
        ? new AWS.S3({ ...s3.config, useAccelerateEndpoint: true })
        : s3;

    const uploader = client.upload(params);

    if (onProgress) {
      uploader.on("httpUploadProgress", (e) => {
        const progress: TransferProgress = {
          loaded: e.loaded,
          total: e.total,
          percent: e.total ? (e.loaded / e.total) * 100 : undefined,
        };
        onProgress(progress);
      });
    }

    const response = await uploader.promise();
    return response;
  } catch (error) {
    throw new S3Error("uploadFile", bucketName, key, error);
  }
};

export const deleteFile = async (
  s3: AWS.S3,
  bucketName: string,
  key: string,
): Promise<AWS.S3.DeleteObjectOutput> => {
  try {
    const params: AWS.S3.DeleteObjectRequest = {
      Bucket: bucketName,
      Key: key,
    };
    const response = await s3.deleteObject(params).promise();
    return response;
  } catch (error) {
    throw new S3Error("deleteFile", bucketName, key, error);
  }
};

export const downloadFile = async (
  s3: AWS.S3,
  bucketName: string,
  key: string,
  options: {
    onProgress?: (progress: TransferProgress) => void;
    useTransferAcceleration?: boolean;
  } = {},
): Promise<Blob> => {
  const { onProgress, useTransferAcceleration = false } = options;

  try {
    const params: AWS.S3.GetObjectRequest = {
      Bucket: bucketName,
      Key: key,
    };

    const client = useTransferAcceleration
      ? new AWS.S3({ ...s3.config, useAccelerateEndpoint: true })
      : s3;

    const request = client.getObject(params);

    if (onProgress) {
      request.on("httpDownloadProgress", (e) => {
        const progress: TransferProgress = {
          loaded: e.loaded,
          total: e.total,
          percent: e.total ? (e.loaded / e.total) * 100 : undefined,
        };
        onProgress(progress);
      });
    }

    const response = await request.promise();
    const blob = new Blob([response.Body as ArrayBuffer], {
      type: response.ContentType || "application/octet-stream",
    });
    return blob;
  } catch (error) {
    throw new S3Error("downloadFile", bucketName, key, error);
  }
};

export const fileExists = async (
  s3: AWS.S3,
  bucketName: string,
  key: string,
): Promise<boolean> => {
  try {
    await s3
      .headObject({
        Bucket: bucketName,
        Key: key,
      })
      .promise();
    return true;
  } catch (error) {
    if ((error as { code: string }).code === "NotFound") {
      return false;
    }
    throw new S3Error("fileExists", bucketName, key, error);
  }
};

export interface ObjectMetadata {
  etag: string | null;
  lastModified: Date | null;
  contentLength: number | null;
}

/**
 * Get object metadata (ETag, LastModified) without downloading the file.
 * Useful for cache validation - compare ETag with cached version.
 */
export const getObjectMetadata = async (
  s3: AWS.S3,
  bucketName: string,
  key: string,
): Promise<ObjectMetadata> => {
  try {
    const response = await s3
      .headObject({
        Bucket: bucketName,
        Key: key,
      })
      .promise();

    return {
      etag: response.ETag ?? null,
      lastModified: response.LastModified ?? null,
      contentLength: response.ContentLength ?? null,
    };
  } catch (error) {
    throw new S3Error("getObjectMetadata", bucketName, key, error);
  }
};

// S3 CopyObject is hard-capped at 5 GiB per request. Larger objects must use
// the multipart copy API (createMultipartUpload + uploadPartCopy +
// completeMultipartUpload). Files that exceed this bypass the single-request
// path in renameFile.
const S3_COPY_OBJECT_MAX_SIZE = 5 * 1024 * 1024 * 1024; // 5 GiB
// Default multipart part size. Chosen to keep typical file-type-change renames
// (a handful of GB) within a small number of parts without being so large that
// each part takes forever to copy.
const MULTIPART_COPY_PART_SIZE = 500 * 1024 * 1024; // 500 MiB
// S3 multipart uploads cannot exceed 10,000 parts. For pathologically large
// objects (approaching the 5 TiB object limit) we scale the part size up to
// stay under this cap.
const S3_MAX_MULTIPART_PARTS = 10_000;

/**
 * Build a {@link AWS.S3.CreateMultipartUploadRequest} that forwards all
 * standard system metadata headers from the source object to the destination.
 *
 * The single-request `CopyObject` path preserves source metadata automatically
 * via S3's default `COPY` metadata directive. The multipart path does NOT —
 * `createMultipartUpload` starts from a blank slate — so we replicate that
 * behavior explicitly to keep the two paths interchangeable. Fields absent on
 * the source are left unset rather than being forwarded as `undefined` so we
 * don't clobber any S3 defaults.
 */
const buildCreateMultipartParamsFromSource = (
  bucketName: string,
  destinationKey: string,
  sourceMetadata: AWS.S3.HeadObjectOutput,
): AWS.S3.CreateMultipartUploadRequest => ({
  Bucket: bucketName,
  Key: destinationKey,
  ...(sourceMetadata.ContentType && {
    ContentType: sourceMetadata.ContentType,
  }),
  ...(sourceMetadata.CacheControl && {
    CacheControl: sourceMetadata.CacheControl,
  }),
  ...(sourceMetadata.ContentDisposition && {
    ContentDisposition: sourceMetadata.ContentDisposition,
  }),
  ...(sourceMetadata.ContentEncoding && {
    ContentEncoding: sourceMetadata.ContentEncoding,
  }),
  ...(sourceMetadata.ContentLanguage && {
    ContentLanguage: sourceMetadata.ContentLanguage,
  }),
  ...(sourceMetadata.Expires && { Expires: sourceMetadata.Expires }),
  ...(sourceMetadata.WebsiteRedirectLocation && {
    WebsiteRedirectLocation: sourceMetadata.WebsiteRedirectLocation,
  }),
  // User metadata (x-amz-meta-*). Only forward when non-empty so we don't
  // leave an empty Metadata map on objects that had none.
  ...(sourceMetadata.Metadata &&
    Object.keys(sourceMetadata.Metadata).length > 0 && {
      Metadata: sourceMetadata.Metadata,
    }),
});

/**
 * Copy an S3 object using the multipart copy API. Required for objects larger
 * than {@link S3_COPY_OBJECT_MAX_SIZE}, which exceed the single-request
 * CopyObject limit.
 *
 * Part copies are issued in parallel — each `uploadPartCopy` is an
 * independent server-side byte-range copy with no data dependency on any
 * other part, so sequencing them wastes wall-clock time. Browsers queue
 * requests above their per-origin connection limit naturally.
 *
 * On any failure after the upload has been initiated, the multipart upload
 * is aborted so we don't leave orphaned part data accruing storage cost.
 * Note that with parallel part copies, when one part rejects, the sibling
 * part copies continue running to completion — S3's `abortMultipartUpload`
 * call reclaims their storage regardless.
 */
const multipartCopyObject = async (
  s3: AWS.S3,
  bucketName: string,
  sourceKey: string,
  destinationKey: string,
  contentLength: number,
  sourceMetadata: AWS.S3.HeadObjectOutput,
): Promise<void> => {
  const partSize = Math.max(
    MULTIPART_COPY_PART_SIZE,
    Math.ceil(contentLength / S3_MAX_MULTIPART_PARTS),
  );
  const numParts = Math.ceil(contentLength / partSize);
  const encodedSourceKey = encodeURIComponent(sourceKey);
  const copySource = `${bucketName}/${encodedSourceKey}`;

  const createResult = await s3
    .createMultipartUpload(
      buildCreateMultipartParamsFromSource(
        bucketName,
        destinationKey,
        sourceMetadata,
      ),
    )
    .promise();

  const uploadId = createResult.UploadId;
  if (!uploadId) {
    throw new Error("createMultipartUpload returned no UploadId");
  }

  try {
    const parts: AWS.S3.CompletedPart[] = await Promise.all(
      Array.from({ length: numParts }, async (_, i) => {
        const partNumber = i + 1;
        const startByte = i * partSize;
        const endByte = Math.min(partNumber * partSize, contentLength) - 1;

        const partResult = await s3
          .uploadPartCopy({
            Bucket: bucketName,
            Key: destinationKey,
            PartNumber: partNumber,
            UploadId: uploadId,
            CopySource: copySource,
            CopySourceRange: `bytes=${startByte}-${endByte}`,
          })
          .promise();

        return {
          ETag: partResult.CopyPartResult?.ETag,
          PartNumber: partNumber,
        };
      }),
    );

    await s3
      .completeMultipartUpload({
        Bucket: bucketName,
        Key: destinationKey,
        UploadId: uploadId,
        MultipartUpload: { Parts: parts },
      })
      .promise();
  } catch (error) {
    try {
      await s3
        .abortMultipartUpload({
          Bucket: bucketName,
          Key: destinationKey,
          UploadId: uploadId,
        })
        .promise();
    } catch (abortError) {
      console.warn(
        `Failed to abort multipart upload ${uploadId} for ${destinationKey}:`,
        abortError,
      );
    }
    throw error;
  }
};

export const renameFile = async (
  s3: AWS.S3,
  bucketName: string,
  oldKey: string,
  newKey: string,
): Promise<void> => {
  try {
    // 1. Fetch source metadata. Doubles as an existence check and provides
    //    ContentLength (to decide between single-request and multipart copy)
    //    and ContentType (to preserve on the destination for the multipart path).
    let sourceMetadata: AWS.S3.HeadObjectOutput;
    try {
      sourceMetadata = await s3
        .headObject({ Bucket: bucketName, Key: oldKey })
        .promise();
    } catch (error) {
      if ((error as { code?: string }).code === "NotFound") {
        throw new Error(`Source file does not exist: ${oldKey}`);
      }
      throw error;
    }

    // 2. Check if destination file already exists
    const destinationExists = await fileExists(s3, bucketName, newKey);
    if (destinationExists) {
      throw new Error(`Destination file already exists: ${newKey}`);
    }

    // 3. Copy file to new location. Single-request CopyObject for objects
    //    <= 5 GiB, multipart copy for anything larger.
    if (sourceMetadata.ContentLength === undefined) {
      // Shouldn't happen for a real object — S3 always returns ContentLength
      // on headObject — but log it if it does so a future 5 GiB-boundary bug
      // is easy to spot rather than silently routing down the wrong path.
      console.warn(
        `headObject returned no ContentLength for ${oldKey}; defaulting to 0 and taking the single-request copy path`,
      );
    }
    const contentLength = sourceMetadata.ContentLength ?? 0;
    if (contentLength > S3_COPY_OBJECT_MAX_SIZE) {
      await multipartCopyObject(
        s3,
        bucketName,
        oldKey,
        newKey,
        contentLength,
        sourceMetadata,
      );
    } else {
      const encodedOldKey = encodeURIComponent(oldKey);
      const copyParams: AWS.S3.CopyObjectRequest = {
        Bucket: bucketName,
        CopySource: `${bucketName}/${encodedOldKey}`,
        Key: newKey,
      };
      await s3.copyObject(copyParams).promise();
    }

    // 4. Verify copy was successful
    const copySuccessful = await fileExists(s3, bucketName, newKey);
    if (!copySuccessful) {
      throw new Error(`Failed to copy file to new location: ${newKey}`);
    }

    // 5. Delete original file
    const deleteParams: AWS.S3.DeleteObjectRequest = {
      Bucket: bucketName,
      Key: oldKey,
    };
    await s3.deleteObject(deleteParams).promise();

    // 6. Verify old file was deleted
    const deleteSuccessful = !(await fileExists(s3, bucketName, oldKey));
    if (!deleteSuccessful) {
      console.warn(`Original file was not deleted: ${oldKey}`);
    }
  } catch (error) {
    if (error instanceof Error) {
      throw new S3Error(
        "renameFile",
        bucketName,
        `${oldKey} → ${newKey}`,
        error,
      );
    }
    throw new S3Error(
      "renameFile",
      bucketName,
      `${oldKey} → ${newKey}`,
      String(error),
    );
  }
};

export const getObject = async (
  s3: AWS.S3,
  bucketName: string,
  key: string,
): Promise<AWS.S3.GetObjectOutput> => {
  try {
    const params: AWS.S3.GetObjectRequest = {
      Bucket: bucketName,
      Key: key,
    };
    return await s3.getObject(params).promise();
  } catch (error) {
    throw new S3Error("getObject", bucketName, key, error);
  }
};

/**
 * Sanitizes a filename for use in Content-Disposition header.
 * - Normalizes Unicode whitespace (e.g., narrow no-break space U+202F from macOS) to regular space
 * - Removes control characters
 * - Escapes quotes to prevent header injection
 * - Removes any remaining non-ASCII characters that can't be represented in ISO-8859-1
 */
export const sanitizeFilenameForHeader = (fileName: string): string => {
  // biome-ignore lint/suspicious/noControlCharactersInRegex: intentionally matches control chars to strip them from Content-Disposition header filenames
  const controlCharsRegex = /[\x00-\x1f\x7f]/g;
  // Regex for Unicode whitespace characters that should be normalized to regular space
  // Includes: narrow no-break space (U+202F), non-breaking space (U+00A0), and other Unicode spaces
  const unicodeWhitespaceRegex =
    /[\u00A0\u1680\u2000-\u200A\u202F\u205F\u3000]/g;

  return fileName
    .replace(unicodeWhitespaceRegex, " ") // Normalize Unicode whitespace to regular space
    .replace(controlCharsRegex, "") // Remove control characters
    .replace(/"/g, '\\"'); // Escape quotes
};

export const getDownloadUrl = (
  s3: AWS.S3,
  bucketName: string,
  key: string,
  options: {
    expiresIn?: number; // seconds, default 900 (15 min)
    fileName?: string; // for Content-Disposition header
  } = {},
): Promise<string> => {
  try {
    const params: AWS.S3.GetObjectRequest & {
      Expires?: number;
      ResponseContentDisposition?: string;
    } = {
      Bucket: bucketName,
      Key: key,
      Expires: options.expiresIn ?? 900,
    };

    if (options.fileName) {
      const sanitizedFileName = sanitizeFilenameForHeader(options.fileName);
      params.ResponseContentDisposition = `attachment; filename="${sanitizedFileName}"`;
    }

    // getSignedUrl is synchronous in AWS SDK v2, wrap in Promise for service wrapper compatibility
    return Promise.resolve(s3.getSignedUrl("getObject", params));
  } catch (error) {
    throw new S3Error("getDownloadUrl", bucketName, key, error);
  }
};

/**
 * Presigned GET URL for previewing a file inline in a new browser tab.
 *
 * Unlike getDownloadUrl (which forces `attachment`), this sets
 * `Content-Disposition: inline` so the browser renders the file in-tab using
 * its own out-of-process viewer (PDF plugin, image renderer). The bytes never
 * touch the portal's JS heap — no Blob, no object URL, no leak — and the tab
 * lives in its own renderer process. `contentType` overrides the stored MIME
 * type so the browser reliably renders rather than downloads. The `fileName`
 * still drives the name the browser uses if the user saves the file.
 */
export const getInlinePreviewUrl = (
  s3: AWS.S3,
  bucketName: string,
  key: string,
  options: {
    expiresIn?: number; // seconds, default 900 (15 min)
    fileName?: string; // for Content-Disposition header (download name)
    contentType?: string; // override response MIME so the browser renders inline
  } = {},
): Promise<string> => {
  try {
    const params: AWS.S3.GetObjectRequest & {
      Expires?: number;
      ResponseContentDisposition?: string;
      ResponseContentType?: string;
    } = {
      Bucket: bucketName,
      Key: key,
      Expires: options.expiresIn ?? 900,
    };

    if (options.fileName) {
      const sanitizedFileName = sanitizeFilenameForHeader(options.fileName);
      params.ResponseContentDisposition = `inline; filename="${sanitizedFileName}"`;
    }

    if (options.contentType) {
      params.ResponseContentType = options.contentType;
    }

    // getSignedUrl is synchronous in AWS SDK v2, wrap in Promise for service wrapper compatibility
    return Promise.resolve(s3.getSignedUrl("getObject", params));
  } catch (error) {
    throw new S3Error("getInlinePreviewUrl", bucketName, key, error);
  }
};
